import { useState, useEffect } from "react";
import * as api from "../../apiService";
import { useToast } from "../../components/Toast";

const MONTHS_LONG  = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const BU_STYLE = {
  AMI: { color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
  AMT: { color: "#0d9488", bg: "#f0fdfa", border: "#99f6e4" },
};

function fmt(val) {
  const n = Number(val);
  if (!n) return "—";
  return n.toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtAed(val) {
  const n = Number(val);
  if (!n) return "—";
  return `AED ${fmt(val)}`;
}

function pct(achieved, target) {
  const a = Number(achieved), t = Number(target);
  if (!t) return null;
  return ((a / t) * 100).toFixed(1);
}

function MonthPicker({ year, month, onChange }) {
  const now = new Date();
  const options = [];
  for (let i = -3; i < 13; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    options.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
  }
  return (
    <select
      value={`${year}-${month}`}
      onChange={e => { const [y, m] = e.target.value.split("-"); onChange(+y, +m); }}
      className="btn"
      style={{ cursor: "pointer" }}>
      {options.map(o => {
        const isFuture = o.year > now.getFullYear() ||
          (o.year === now.getFullYear() && o.month > now.getMonth() + 1);
        return (
          <option key={`${o.year}-${o.month}`} value={`${o.year}-${o.month}`}>
            {MONTHS_SHORT[o.month - 1]} {o.year}{isFuture ? " — Forecast" : ""}
          </option>
        );
      })}
    </select>
  );
}

/** Company-level targets panel: Target Value, Target Qty, Achieved Value, Achieved Qty + % */
function TargetsPanel({ company, isEditable, onSaveTarget }) {
  const showToast = useToast();
  const [tvInput, setTvInput] = useState(company.targetValue ?? "");
  const [tqInput, setTqInput] = useState(company.targetQty   ?? "");
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);

  useEffect(() => {
    setTvInput(company.targetValue ?? "");
    setTqInput(company.targetQty   ?? "");
    setSaved(false);
  }, [company.targetValue, company.targetQty, company.companyId]);

  const achievedValue = Number(company.totalDebit) || 0;
  const valuePct      = pct(achievedValue, tvInput || company.targetValue);
  // Qty achieved is manual for now — Tally vouchers don't expose qty in Voucher Register export
  const achievedQty   = null;

  async function handleSave() {
    setSaving(true);
    try {
      await onSaveTarget(company.companyId, tvInput || null, tqInput || null);
      showToast("Targets saved");
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  }

  const inputStyle = {
    fontSize: 12, padding: "4px 8px", border: "1px solid #d1d5db",
    borderRadius: 4, width: 120, background: "white",
  };

  const cellStyle = { background: "#faf8ff", padding: "8px 12px", textAlign: "center" };
  const labelStyle = { fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 };

  return (
    <div style={{
      display: "grid", gridTemplateColumns: "repeat(5, 1fr)",
      gap: 1, background: "#ece8f5", borderBottom: "1px solid var(--color-border)",
    }}>
      {/* Target Value */}
      <div style={cellStyle}>
        <div style={labelStyle}>Target Value</div>
        {isEditable
          ? <input type="number" min="0" step="0.01" value={tvInput}
              onChange={e => setTvInput(e.target.value)} placeholder="AED" style={inputStyle} />
          : <div style={{ fontSize: 12, color: company.targetValue ? "#111827" : "#d1d5db", fontWeight: company.targetValue ? 600 : 400 }}>
              {company.targetValue ? fmtAed(company.targetValue) : "—"}
            </div>
        }
      </div>

      {/* Target Qty */}
      <div style={cellStyle}>
        <div style={labelStyle}>Target Qty (Kg)</div>
        {isEditable
          ? <input type="number" min="0" step="0.001" value={tqInput}
              onChange={e => setTqInput(e.target.value)} placeholder="Kg" style={inputStyle} />
          : <div style={{ fontSize: 12, color: company.targetQty ? "#111827" : "#d1d5db", fontWeight: company.targetQty ? 600 : 400 }}>
              {company.targetQty ? `${fmt(company.targetQty)} Kg` : "—"}
            </div>
        }
      </div>

      {/* Achieved Value */}
      <div style={cellStyle}>
        <div style={labelStyle}>Achieved Value</div>
        <div style={{ fontSize: 12, color: achievedValue ? "#1d4ed8" : "#d1d5db", fontWeight: achievedValue ? 600 : 400 }}>
          {achievedValue ? fmtAed(achievedValue) : "—"}
        </div>
        {valuePct !== null && (
          <div style={{
            fontSize: 10, marginTop: 2, fontWeight: 700,
            color: Number(valuePct) >= 100 ? "#15803d" : Number(valuePct) >= 75 ? "#b45309" : "#b91c1c",
          }}>
            {valuePct}%
          </div>
        )}
      </div>

      {/* Achieved Qty */}
      <div style={cellStyle}>
        <div style={labelStyle}>Achieved Qty (Kg)</div>
        <div style={{ fontSize: 12, color: "#d1d5db" }}>—</div>
        <div style={{ fontSize: 10, marginTop: 2, color: "#9ca3af" }}>Manual entry</div>
      </div>

      {/* Save button (future months only) */}
      <div style={{ ...cellStyle, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {isEditable ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}
              style={{ fontSize: 11, padding: "4px 10px" }}>
              {saving ? "Saving…" : "Save Targets"}
            </button>
            {saved && <span style={{ fontSize: 10, color: "#15803d" }}>✓ Saved</span>}
          </div>
        ) : (
          <span style={{ fontSize: 10, color: "#9ca3af" }}>
            {company.targetValue || company.targetQty ? "Targets locked" : "No targets set"}
          </span>
        )}
      </div>
    </div>
  );
}

/** Company panel — 5-column table + targets panel above */
function CompanyPanel({ company, spEmail, year, month, isFuture, isCurrentMonth, canEdit, onSaveForecastBatch, onSaveTarget }) {
  const showToast = useToast();
  const s = BU_STYLE[company.businessUnit] ?? BU_STYLE.AMI;
  const customers = company.customers ?? [];
  const [search, setSearch] = useState("");

  // SPs: editable only until first save (one-shot lock). Managers can always re-edit.
  const anyForecastSaved = customers.some(c => c.salesForecast != null);
  const forecastEditable = isFuture || (isCurrentMonth && !anyForecastSaved);
  const targetEditable   = false; // targets are managed via Sales Targets page

  // In view mode (not forecast-entry), hide customers with no activity so the list is shorter at a glance.
  // Search always bypasses this so any customer can still be found.
  const hasActivity = c =>
    Number(c.salesForecast) > 0 || Number(c.collectionForecast) > 0 ||
    Number(c.debit) > 0        || Number(c.credit) > 0;
  const activeCustomers = (!isFuture && !forecastEditable) ? customers.filter(hasActivity) : customers;
  const hiddenCount     = customers.length - activeCustomers.length;

  const visibleCustomers = search.trim()
    ? customers.filter(c => c.ledgerName.toLowerCase().includes(search.trim().toLowerCase()))
    : activeCustomers;

  // edits: only stores values the user has typed in this session — ledgerName → { salesForecast?, collectionForecast? }
  // Server values (c.salesForecast) are read directly from props; edits overlay on top.
  const [edits,  setEdits]  = useState({});
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);

  useEffect(() => {
    setEdits({});
    setSaved(false);
  }, [company.companyId, year, month]);

  function getSales(c) { return edits[c.ledgerName]?.salesForecast ?? c.salesForecast ?? ""; }

  function setField(ledgerName, value) {
    setEdits(prev => ({ ...prev, [ledgerName]: { salesForecast: value } }));
  }

  async function handleSaveForecasts() {
    setSaving(true);
    try {
      const rows = customers.map(c => ({
        ledgerName:    c.ledgerName,
        salesForecast: getSales(c) !== "" ? getSales(c) : null,
      }));
      await onSaveForecastBatch(company.companyId, rows);
      showToast("Forecasts saved");
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  }

  const inputStyle = {
    fontSize: 11, padding: "3px 6px", border: "1px solid #d1d5db",
    borderRadius: 3, width: "100%", background: "white", textAlign: "right",
  };

  return (
    <div className="card" style={{ marginBottom: 0, flex: 1, minWidth: 0 }}>
      {/* Company badge header */}
      <div style={{
        padding: "9px 14px", display: "flex", alignItems: "center", gap: 10,
        background: s.bg, borderBottom: `1px solid ${s.border}`,
      }}>
        <span style={{
          fontWeight: 700, fontSize: 12, color: s.color,
          background: "white", border: `1px solid ${s.border}`,
          borderRadius: 4, padding: "2px 8px", letterSpacing: "0.04em",
        }}>
          {company.businessUnit}
        </span>
        <span style={{ fontSize: 12, color: s.color, fontWeight: 600 }}>
          {search.trim()
            ? `${visibleCustomers.length} / ${customers.length} customers`
            : hiddenCount > 0
              ? `${activeCustomers.length} active · ${hiddenCount} hidden`
              : `${customers.length} customer${customers.length !== 1 ? "s" : ""}`}
        </span>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search customer…"
          style={{
            fontSize: 12, padding: "3px 8px", border: `1px solid ${s.border}`,
            borderRadius: 4, background: "white", color: "#374151",
            width: 160, outline: "none",
          }}
        />
        <span style={{ marginLeft: "auto", fontSize: 11, color: s.color, opacity: 0.7 }}>
          {fmtAed(company.totalDebit)} sales · {fmtAed(company.totalCredit)} receipts
        </span>
      </div>

      {/* Targets row */}
      <TargetsPanel
        company={company}
        isEditable={targetEditable}
        onSaveTarget={(companyId, tv, tq) => onSaveTarget(companyId, tv, tq)}
      />

      {/* Customer table */}
      <div className="table-wrap" style={{ maxHeight: "none" }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th style={{ textAlign: "right", width: 120 }}>Sales (Forecast)</th>
              <th style={{ textAlign: "right", width: 120 }}>Sales (Actual)</th>
              <th style={{ textAlign: "right", width: 120 }}>Collection (Due/Overdue)</th>
              <th style={{ textAlign: "right", width: 120 }}>Collection (Actual)</th>
            </tr>
          </thead>
          <tbody>
            {visibleCustomers.length === 0 && (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", color: "#9ca3af", padding: 24 }}>
                  {search.trim()
                    ? `No customers matching "${search}".`
                    : forecastEditable
                      ? "No customers loaded — refresh the ledger cache first."
                      : hiddenCount > 0
                        ? `All ${customers.length} customers have no activity this month. Use search to find one.`
                        : "No transactions this month."}
                </td>
              </tr>
            )}
            {visibleCustomers.map(c => (
              <tr key={c.ledgerName}>
                <td className="fw-600" style={{ maxWidth: 220 }}>
                  <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    title={c.ledgerName}>
                    {c.ledgerName}
                  </span>
                </td>

                {/* Sales Forecast */}
                <td style={{ textAlign: "right", padding: "6px 8px" }}>
                  {forecastEditable ? (
                    <input
                      type="number" min="0" step="0.01"
                      value={getSales(c)}
                      onChange={e => setField(c.ledgerName, e.target.value)}
                      placeholder="0.00"
                      style={inputStyle}
                    />
                  ) : (
                    <span style={{ fontSize: 12, color: c.salesForecast ? "#6b7280" : "#d1d5db",
                      fontVariantNumeric: "tabular-nums" }}>
                      {c.salesForecast ? fmt(c.salesForecast) : "—"}
                    </span>
                  )}
                </td>

                {/* Sales Actual */}
                <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums",
                  color: Number(c.debit) > 0 ? "#1d4ed8" : "#9ca3af" }}>
                  {fmtAed(c.debit)}
                </td>

                {/* Collection Due/Overdue — auto-computed from Tally bills, always read-only */}
                <td style={{ textAlign: "right", padding: "6px 8px" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                    <span style={{ fontSize: 12, color: c.collectionForecast ? "#6b7280" : "#d1d5db",
                      fontVariantNumeric: "tabular-nums" }}>
                      {c.collectionForecast ? fmt(c.collectionForecast) : "—"}
                    </span>
                    {c.collectionForecast && (
                      <span style={{ fontSize: 9, color: "#9ca3af", fontStyle: "italic" }}>From Tally</span>
                    )}
                  </div>
                </td>

                {/* Collection Actual */}
                <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums",
                  color: Number(c.credit) > 0 ? "#15803d" : "#9ca3af" }}>
                  {fmtAed(c.credit)}
                </td>
              </tr>
            ))}
          </tbody>
          {customers.length > 0 && (
            <tfoot>
              <tr style={{ fontWeight: 700 }}>
                <td style={{ fontSize: 12, color: "#374151" }}>Total</td>
                <td style={{ textAlign: "right", color: "#6b7280", fontVariantNumeric: "tabular-nums", fontSize: 12 }}>
                  {(() => {
                    const total = customers.reduce((s, c) => s + Number(getSales(c) || 0), 0);
                    return total ? fmt(total) : "—";
                  })()}
                </td>
                <td style={{ textAlign: "right", color: "#1d4ed8", fontVariantNumeric: "tabular-nums" }}>
                  {fmtAed(company.totalDebit)}
                </td>
                <td style={{ textAlign: "right", color: "#6b7280", fontVariantNumeric: "tabular-nums", fontSize: 12 }}>
                  {(() => {
                    const total = customers.reduce((s, c) => s + Number(c.collectionForecast || 0), 0);
                    return total ? fmt(total) : "—";
                  })()}
                </td>
                <td style={{ textAlign: "right", color: "#15803d", fontVariantNumeric: "tabular-nums" }}>
                  {fmtAed(company.totalCredit)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Save forecasts button — shown for future months always, and for current month until saved */}
      {forecastEditable && customers.length > 0 && (
        <div style={{ padding: "10px 14px", borderTop: "1px solid var(--color-border)", display: "flex", alignItems: "center", gap: 8 }}>
          <button className="btn btn-primary" onClick={handleSaveForecasts} disabled={saving}
            style={{ fontSize: 12, padding: "5px 14px" }}>
            {saving ? "Saving…" : "Save All Forecasts"}
          </button>
          {isCurrentMonth && !anyForecastSaved && (
            <span style={{ fontSize: 11, color: "#b45309" }}>Once saved, forecasts cannot be changed for this month.</span>
          )}
        </div>
      )}
      {saved && (
        <div style={{ padding: "6px 14px", borderTop: "1px solid var(--color-border)", fontSize: 12, color: "#15803d" }}>
          ✓ Forecasts saved
        </div>
      )}
    </div>
  );
}

function CommentsSection({ year, month, canManage }) {
  const showToast = useToast();
  const [comments,  setComments]  = useState([]);
  const [text,      setText]      = useState("");
  const [saving,    setSaving]    = useState(false);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    setLoading(true);
    api.fetchMonthlyReportComments(year, month)
      .then(setComments)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [year, month]);

  async function handleAdd() {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.addMonthlyReportComment(year, month, text.trim());
      setText("");
      const fresh = await api.fetchMonthlyReportComments(year, month);
      setComments(fresh);
      showToast("Comment added");
    } catch {
      showToast("Failed to add comment");
    } finally {
      setSaving(false);
    }
  }

  function fmtTs(ts) {
    if (!ts) return "";
    try {
      return new Date(ts).toLocaleString("en-AE", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit"
      });
    } catch { return String(ts); }
  }

  return (
    <div className="card" style={{ marginTop: 24, padding: "14px 18px" }}>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, color: "#1d4ed8" }}>
        Management Comments
      </div>

      {loading && <div style={{ fontSize: 12, color: "#9ca3af" }}>Loading…</div>}

      {!loading && comments.length === 0 && (
        <div style={{ fontSize: 12, color: "#9ca3af", fontStyle: "italic" }}>No comments yet.</div>
      )}

      {comments.map(c => (
        <div key={c.id} style={{
          marginBottom: 12, borderLeft: "3px solid #1d4ed8",
          paddingLeft: 10, paddingTop: 2, paddingBottom: 2,
        }}>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 3 }}>
            <strong style={{ color: "#374151" }}>{c.created_by}</strong>
            &nbsp;·&nbsp;{fmtTs(c.created_at)}
          </div>
          <div style={{ fontSize: 13, color: "#111827", whiteSpace: "pre-wrap" }}>{c.comment}</div>
        </div>
      ))}

      {canManage && (
        <div style={{ marginTop: 12, borderTop: "1px solid var(--color-border)", paddingTop: 12 }}>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Add a management comment…"
            rows={3}
            style={{
              width: "100%", fontSize: 12, padding: "6px 8px",
              border: "1px solid #d1d5db", borderRadius: 4,
              resize: "vertical", boxSizing: "border-box",
            }}
          />
          <div style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center" }}>
            <button
              className="btn btn-primary"
              onClick={handleAdd}
              disabled={saving || !text.trim()}
              style={{ fontSize: 12, padding: "5px 14px" }}>
              {saving ? "Saving…" : "Add Comment"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function PageMonthlyReports({ myPerms = [], userEmail = "" }) {
  const now = new Date();
  const [year,      setYear]      = useState(now.getFullYear());
  const [month,     setMonth]     = useState(now.getMonth() + 1);
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const nowYear  = now.getFullYear();
  const nowMonth = now.getMonth() + 1;
  const isFuture       = year > nowYear || (year === nowYear && month > nowMonth);
  const isCurrentMonth = year === nowYear && month === nowMonth;

  useEffect(() => {
    setLoading(true); setError(null); setData(null); setActiveTab(0);
    api.fetchMonthlyReport(year, month)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [year, month]);

  const canManage    = myPerms.includes("CRM_MANAGE");
  const allSps       = data?.salespersons ?? [];
  const salespersons = canManage ? allSps : allSps.filter(sp => sp.userEmail === userEmail);
  const activeSp     = salespersons[activeTab];

  async function handleSaveForecastBatch(companyId, rows) {
    await api.saveForecastBatch(year, month, activeSp.userEmail, companyId, rows);
    const fresh = await api.fetchMonthlyReport(year, month);
    setData(fresh);
  }

  async function handleSaveTarget(companyId, targetValue, targetQty) {
    await api.saveMonthlyTarget(year, month, activeSp.userEmail, companyId, targetValue, targetQty);
    const fresh = await api.fetchMonthlyReport(year, month);
    setData(fresh);
  }

  return (
    <div style={{ padding: "20px 24px" }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 16 }}>
        <div className="page-header-left">
          <div className="page-title">Monthly Reports</div>
          <div className="page-subtitle" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span>
              {isFuture ? "Forecast entry — " : "Sales & receipts — "}
              {MONTHS_LONG[month - 1]} {year}
            </span>
            {data && !isFuture && (
              data.cachedAt
                ? <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4,
                    background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" }}>
                    Synced {new Date(data.cachedAt).toLocaleTimeString("en-AE", { hour:"2-digit", minute:"2-digit" })}
                  </span>
                : data.cached
                  ? <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4,
                      background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0" }}>Cached</span>
                  : <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4,
                      background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" }}>Live from Tally</span>
            )}
            {isFuture && (
              <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4,
                background: "#fefce8", color: "#a16207", border: "1px solid #fde68a" }}>Forecast Period</span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <MonthPicker year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} />
          <button
            className="btn"
            disabled={isFuture || isCurrentMonth}
            onClick={() => api.downloadPdf(
              `/api/crm/monthly-report/pdf?year=${year}&month=${month}${activeSp ? `&spEmail=${encodeURIComponent(activeSp.userEmail)}` : ""}`,
              `Monthly-Report-${year}-${String(month).padStart(2, "0")}.pdf`
            )}
            style={{ fontSize: 12, padding: "5px 14px", whiteSpace: "nowrap" }}>
            Download PDF
          </button>
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: "48px 0", color: "#9ca3af", fontSize: 13 }}>
          Loading…
        </div>
      )}

      {error && <div className="alert-warn" style={{ padding: 10, marginBottom: 16 }}>{error}</div>}

      {!loading && data && salespersons.length === 0 && (
        <div style={{ textAlign: "center", padding: "48px 0", color: "#9ca3af", fontSize: 13 }}>
          No data for {MONTHS_LONG[month - 1]} {year}.
        </div>
      )}

      {/* Salesperson tabs */}
      {!loading && salespersons.length > 0 && (
        <>
          <div style={{
            display: "flex", gap: 0, borderBottom: "2px solid var(--color-border)",
            marginBottom: 20, overflowX: "auto",
          }}>
            {salespersons.map((sp, i) => (
              <button key={sp.userEmail} onClick={() => setActiveTab(i)} style={{
                padding: "8px 18px", fontSize: 13, fontWeight: activeTab === i ? 700 : 400,
                background: "none", border: "none", cursor: "pointer",
                color: activeTab === i ? "#1d4ed8" : "#374151",
                borderBottom: activeTab === i ? "2px solid #1d4ed8" : "2px solid transparent",
                marginBottom: -2, whiteSpace: "nowrap",
              }}>
                {sp.userName}
              </button>
            ))}
          </div>

          {activeSp && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(560px, 1fr))", gap: 16 }}>
              {activeSp.companies.map(company => (
                <CompanyPanel
                  key={company.companyId}
                  company={company}
                  spEmail={activeSp.userEmail}
                  year={year}
                  month={month}
                  isFuture={isFuture}
                  isCurrentMonth={isCurrentMonth}
                  canEdit={canManage}
                  onSaveForecastBatch={(companyId, rows) =>
                    handleSaveForecastBatch(companyId, rows)}
                  onSaveTarget={(companyId, tv, tq) =>
                    handleSaveTarget(companyId, tv, tq)}
                />
              ))}
            </div>
          )}
          <CommentsSection
            year={year}
            month={month}
            canManage={canManage}
          />
        </>
      )}
    </div>
  );
}
